import type IRWSConfig from './types/IRWSConfig';
import type { RWSPluginEntry } from './types/IRWSConfig';

import type { RWSNotify } from './types/RWSNotify';

import type { ConfigServiceInstance } from './services/ConfigService';
import ConfigService from './services/ConfigService';
import type { DOMServiceInstance } from './services/DOMService';
import DOMService from './services/DOMService';
import type { NotifyServiceInstance } from './services/NotifyService';
import NotifyService from './services/NotifyService';

import type IRWSUser from './types/IRWSUser';
import type { RWSWindow } from './types/RWSWindow';
import { type RWSWindowComponentRegister, loadRWSRichWindow } from './types/RWSWindow';

import type { Container} from './components/_container';
import { DI, Registration } from './components/_container';

import RWSViewComponent, { type IWithCompose } from './components/_component';
import RWSContainer from './components/_container';
import type TheRWSService from './services/_service';

import ServicesHelper from './client/services';
import ConfigHelper from './client/config';
import type { DefaultRWSPluginOptionsType, RWSPlugin } from './plugins/_plugin';
import type { IStaticRWSPlugin } from './types/IRWSPlugin'

interface IHotModule extends NodeModule {
    hot?: {
        accept(dependencies: string[], callback?: (updatedDependencies: string[]) => void): void;
        accept(dependency: string, callback?: () => void): void;
        accept(errHandler?: (err: Error) => void): void;
        dispose(callback: (data: any) => void): void;
    }
}

type RWSEventListener = (event: CustomEvent) => void;

class RWSClient {
    protected _container: Container;
    protected user: IRWSUser = null;

    protected config: IRWSConfig = {};
    protected plugins: {[key: string]: RWSPlugin<DefaultRWSPluginOptionsType>} = {}
    protected isSetup = false;
    protected devStorage: { [key: string]: any } = {};
    protected customServices: { [serviceName: string]: TheRWSService} = {};
    protected defaultServices: { [serviceName: string]: TheRWSService} = {};

    private servicesHelper = ServicesHelper.bind(this)();
    private configHelper = ConfigHelper.bind(this)();

    protected initCallback: () => Promise<void> = async () => { };

    constructor(
        @ConfigService public appConfig: ConfigServiceInstance,
        @DOMService public domService: DOMServiceInstance,
        @NotifyService public notifyService: NotifyServiceInstance
    ) {
        this._container = RWSContainer();
        this.user = this.getUser();

        this.loadServices();

        this.config.plugins = [];
    }

    addPlugin<T extends DefaultRWSPluginOptionsType>(pluginEntry: IStaticRWSPlugin)
    {
        this.config.plugins.push(pluginEntry);
    }

    async setup(config: IRWSConfig = {}): Promise<IRWSConfig> {
        return this.configHelper.setup(config);
    }

    async start(config: IRWSConfig = {}): Promise<RWSClient> {
        return this.configHelper.start(config);
    }

    private loadServices(){
        return this.servicesHelper.loadServices();
    }

    get(key: string): any | null
    {
        return this.configHelper.get(key);
    }

    setNotifier(notifier: RWSNotify): RWSClient {
        this.notifyService.setNotifier(notifier);

        return this;
    }

    setDefaultLayout(DefaultLayout: any): RWSClient {
        this.config.defaultLayout = DefaultLayout;

        return this;
    }

    getUser(): IRWSUser {
        return this.configHelper.getUser();
    }

    setUser(user: IRWSUser): RWSClient {
        return this.configHelper.setUser(user);
    }

    getConfig(): ConfigServiceInstance {
        return this.appConfig;
    }

    on<T>(eventName: string, listener: RWSEventListener): void {
        document.addEventListener(eventName, (event: Event) => {
            listener(event as CustomEvent<T>);
        });
    }

    setDevStorage(key: string, stuff: any): RWSClient {
        this.devStorage[key] = stuff;
        return this;
    }

    getDevStorage(key: string): any {
        return this.devStorage[key];
    }

    registerToDI(): void {

    }

    async onDOMLoad(): Promise<void> {
        return this.domService.onDOMLoad()
    }

    assignClientToBrowser(): void {
        this.getBrowserObject().RWS.client = this;
    }

    enableRouting(): void {
        this.appConfig.mergeConfig({ routing_enabled: true });
    }

    disableRouting(): void {
        this.appConfig.mergeConfig({ routing_enabled: false });
    }

    private getBrowserObject(): RWSWindow {
        loadRWSRichWindow();
        return window;
    }

    static getDI(): typeof DI {
        return DI;
    }

    logout(){
        this.user = null;
    }
}

export default DI.createInterface<RWSClient>(x => x.singleton(RWSClient));
export type { IHotModule };
export { RWSClient as RWSClientInstance };